import { INotificationRepository } from './INotificationRepository';
import type BaseApiProvider from 'providers/BaseApiProvider';
import { injectable, inject } from 'inversify';
import { ApiResponseModel } from 'models/ApiResponseModel';
import UserLoginModel from 'models/auth/UserLoginModel';
import Logger from 'utilities/log';
import { AuthRoleModel, AuthUserGetPaload, AuthUserPayloadModel, AuthUserSimpleModel } from 'models/auth/AuthUserModels';
import { PaginationModel } from 'models/PaginationModel';
import { NewNotificationGetPayloadModel, NewNotificationModel, NewNotificationPayloadModel, NotificationDetailGetPayloadModel, NotificationDetailModel, NotificationGetPayloadModel, NotificationModel, NotificationOrEventTypes, 
    NotificationPayloadModel, NotificationUserGetPayloadModel } from 'models/notification/notification';
import { CurrentUserFollowUpModel } from 'models/notification/UserFollowUp';

@injectable()
class NotificationRepository implements INotificationRepository {

    @inject(Symbol.for("BaseApiProvider_5080")) private _provider?: BaseApiProvider;


    public async GetAllNotification(params: NotificationGetPayloadModel): Promise<{
        rows: NotificationModel[];
        pagination: PaginationModel;
    }> {
        try {
            const url = `Notification/info/GetAllNotification`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to GetAllNotification');
                return { pagination: { nowpage: 0, hasnext: false, nextpage: 0, previewpage: 0, totalcount: 0 }, rows: [] };
            }

            return {
                rows: response["data"]["rows"].map((js: any) => NotificationModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to GetAllNotification: ${error}`);
            throw new Error(`Failed to GetAllNotification: ${error}`);
        }
    }


    public async GetNotificationUserDetail(params: NotificationDetailGetPayloadModel): Promise<{
        rows: NotificationDetailModel[];
        pagination: PaginationModel;
    }> {
        try {
            const url = `Notification/info/GetNotificationUserDetail`;
            const formData = new FormData();
            Object.entries(params).forEach(([key, value]) => {
                formData.append(key, value.toString());
            });
            const response = await this._provider!.getForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to GetNotificationUserDetail');
                return { pagination: { nowpage: 0, hasnext: false, nextpage: 0, previewpage: 0, totalcount: 0 }, rows: [] };
            }

            return {
                rows: response["data"]["rows"].map((js: any) => NotificationDetailModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to GetNotificationUserDetail: ${error}`);
            throw new Error(`Failed to GetNotificationUserDetail: ${error}`);
        }
    }

    public async GetCurrentUserNotificatios(params: NotificationUserGetPayloadModel): Promise<{
        rows: NotificationModel[];
        pagination: PaginationModel;
    }> {
        try {
            const url = `Notification/info/GetCurrentUserNotificatios`;
            const formData = new FormData();
            Object.entries(params).forEach(([key, value]) => {
                formData.append(key, value.toString());
            });
            const response = await this._provider!.getForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to GetCurrentUserNotificatios');
                return { pagination: { nowpage: 0, hasnext: false, nextpage: 0, previewpage: 0, totalcount: 0 }, rows: [] };
            }

            return {
                rows: response["data"]["rows"].map((js: any) => NotificationModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to GetCurrentUserNotificatios: ${error}`);
            throw new Error(`Failed to GetCurrentUserNotificatios: ${error}`);
        }
    }

    public async GetUserUnseenNotifCount(NotificationOrEvent: NotificationOrEventTypes): Promise<number> {
        try {
            const url = `Notification/info/GetUserUnseenNotifCount`;
            const formData = new FormData();
            formData.append("NotificationOrEvent", NotificationOrEvent.toString());
            const response = await this._provider!.getForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get accounts list');
                return 0;
            }
            return response["data"];
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to delete item request detail: ${error}`);
            throw new Error(`Failed to delete item request detail: ${error}`);
        }
    }

    public async GetCurrentUserFollowUp(date: string): Promise<{
        rows: CurrentUserFollowUpModel[];
        pagination: PaginationModel;
    }> {
        try {
            const url = `Notification/info/GetCurrentUserFollowUp`;
            const formData = new FormData();
            formData.append('date', date.toString());
            const response = await this._provider!.getForm(url, formData);
             const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to GetAllNotification');
                return { pagination: { nowpage: 0, hasnext: false, nextpage: 0, previewpage: 0, totalcount: 0 }, rows: [] };
            }

            return {
                rows: response["data"]["rows"].map((js: any) => CurrentUserFollowUpModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to GetAllNotification: ${error}`);
            throw new Error(`Failed to GetAllNotification: ${error}`);
        }
    }

    public async SetNotification(params: NotificationPayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `Notification/Action/SetNotification`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to SetNotification: ${error}`);
            throw new Error(`Failed to SetNotification: ${error}`);
        }
    }
    public async SaveNotificationInfo(params: NewNotificationPayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/NotificationAction/SaveNotificationInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to SetNotification: ${error}`);
            throw new Error(`Failed to SetNotification: ${error}`);
        }
    }

    public async DeleteNotificationInfo(Id: number): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/NotificationAction/DeleteNotificationInfo`;
            const formData = new FormData();
            formData.append('Id', Id.toString());
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to DeleteNotification: ${error}`);
            throw new Error(`Failed to DeleteNotification: ${error}`);
        }
    }

    public async SeenNotification(Id: number): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/NotificationAction/SeenNotification`;
            const formData = new FormData();
            formData.append('Id', Id.toString());
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to SeenNotification: ${error}`);
            throw new Error(`Failed to SeenNotification: ${error}`);
        }
    }

    public async GetNewCurrentUserNotifications(params: NotificationUserGetPayloadModel): Promise<{
        rows: NewNotificationModel[];
        pagination: PaginationModel;
    }> {
        try {
            const url = `Api/NotificationInfo/GetCurrentUserNotifications`;
            const formData = new FormData();
            Object.entries(params).forEach(([key, value]) => {
                formData.append(key, value.toString());
            });
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to GetCurrentUserNotifications');
                return { pagination: { nowpage: 0, hasnext: false, nextpage: 0, previewpage: 0, totalcount: 0 }, rows: [] };
            }

            return {
                rows: response["data"]["rows"].map((js: any) => NewNotificationModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to GetCurrentUserNotifications: ${error}`);
            throw new Error(`Failed to GetCurrentUserNotifications: ${error}`);
        }
    }

    public async GetAllNotificationInfo(params: NewNotificationGetPayloadModel): Promise<{
        rows: NewNotificationModel[];
        pagination: PaginationModel;
    }> {
        try {
            const url = `Api/NotificationInfo/GetAllNotificationInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get notifications list');
            }

            return {
                rows: response["data"]["rows"].map((js: any) => NewNotificationModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get notifications list: ${error}`);
            throw new Error(`Failed to get users list: ${error}`);
        }
    }
    public async GetNotificationInfo(params: NewNotificationGetPayloadModel): Promise<NewNotificationModel| null> {
        try {
            const url = `Api/NotificationInfo/GetNotificationInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get notification info');
                return null;
            }
            return NewNotificationModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }

    public async GetNewUserUnseenNotifCount(ForAdmin: boolean): Promise<number> {
        try {
            const url = `Api/NotificationInfo/GetUserUnseenNotifCount`;
            const formData = new FormData();
            formData.append("ForAdmin", ForAdmin.toString());
            const response = await this._provider!.getForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get unseen notif count');
                return 0;
            }
            return response["data"];
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to unseen notif count: ${error}`);
            throw new Error(`Failed to delete item request detail: ${error}`);
        }
    }

}

export default NotificationRepository;
